import React, { useMemo, useState } from "react";
import {
    useJobSectorsQuery,
    useDeleteJobSectorMutation,
} from "../../services/basicData/basicData";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import { HugeiconsIcon } from "@hugeicons/react";
import {
    DeleteIcon,
    Edit02Icon,
    AddSquareIcon,
    ViewIcon,
} from "@hugeicons/core-free-icons";
import { TableC } from "../../CommonElements/TableC";
import BtnAsLink from "../../Component/BtnAsLink";
import { Briefcase01FreeIcons } from "@hugeicons/core-free-icons/index";
import { Tag } from "antd";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";

// Empty state component for when no jobSector are found
const EmptyState = ({ hasSearch }) => {
    const { t } = useTranslation();

    return (
        <div className="empty-state text-center py-5">
            <div className="empty-state-icon mb-3">
                <HugeiconsIcon
                    icon={Briefcase01FreeIcons}
                    size={48}
                    className="text-muted"
                />
            </div>
            <h4 className="empty-state-title mb-2">{t("NO_JOB_SECTORS_FOUND")}</h4>
            <p className="empty-state-description text-muted mb-4">
                {t("NO_JOBS_DESCRIPTION")}
            </p>
            {!hasSearch && (
                <Link to="create" className="btn btn-primary">
                    <HugeiconsIcon
                        icon={Briefcase01FreeIcons}
                        size={16}
                        className="me-2"
                    />
                    {t("CREATE_JOB")}
                </Link>
            )}
        </div>
    );
};

const PER_PAGE = 10;

export const JobSectors = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState({});
    const { t } = useTranslation();

    const { confirm, ModalComponent } = useConfirm();

    const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch("", 500);

    const queryParams = useMemo(() => {
        const params = {
            page: currentPage,
            per_page: PER_PAGE,
            ...(debouncedValue?.trim() ? { search: debouncedValue.trim() } : {}),
            ...Object.fromEntries(
                Object.entries(filters).filter(
                    ([_, value]) => value !== "" && value !== null && value !== undefined
                )
            ),
        };
        return params;
    }, [currentPage, debouncedValue, filters]);

    const { mutate: deleteJobSector, isPending: isDeletingJobSector } = useDeleteJobSectorMutation();
    const { data, isLoading, isFetching } = useJobSectorsQuery(queryParams);
    const loading = isLoading || isFetching;
    const jobSectors = data?.data?.data ?? [];
    const meta = data?.data?.meta ?? {};

    const handleSearchChange = (e) => {
        setSearchValue(e.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleFilterApply = (filterData) => {
        setFilters(filterData);
        setCurrentPage(1);
    };

    const handleFilterReset = () => {
        setFilters({});
        setCurrentPage(1);
    };

    const filterFields = useMemo(
        () => [
            {
                name: "is_enabled_for_makkah",
                label: t("ENABLED_FOR_MAKKAH"),
                type: "select",
                options: [
                    { label: t("ENABLED"), value: true },
                    { label: t("DISABLED"), value: false },
                ],
            },
            {
                name: "is_enabled_for_madina",
                label: t("ENABLED_FOR_MADINA"),
                type: "select",
                options: [
                    { label: t("ENABLED"), value: true },
                    { label: t("DISABLED"), value: false },
                ],
            },
        ],
        [t]
    );

    const columns = [
        {
            name: t("NAME_AR"),
            key: "name_ar",
        },
        {
            name: t("NAME_EN"),
            key: "name_en",
        },
        {
            name: t("ENABLED_FOR_MAKKAH"),
            key: "is_enabled_for_makkah",
            render: (value) => <Tag color={!!value ? "green" : "red"}>{!!value ? t("ENABLED") : t("DISABLED")}</Tag>,
        },
        {
            name: t("ENABLED_FOR_MADINA"),
            key: "is_enabled_for_madina",
            render: (value) => <Tag color={!!value ? "green" : "red"}>{!!value ? t("ENABLED") : t("DISABLED")}</Tag>,
        },
        { name: t("ACTIONS"), key: "actions", filterable: false },
    ]

    // Define actions function
    const getActions = (rowData) => {
        const { id } = rowData;
        return [
            {
                key: "view",
                label: (
                    <Link
                        to={`${id}/view`}
                        className="d-flex align-items-center gap-2"
                    >
                        <HugeiconsIcon size={16} icon={ViewIcon} />
                        {t("VIEW")}
                    </Link>
                ),
            },
            {
                key: "edit",
                label: (
                    <Link
                        to={`${id}/edit`}
                        className="d-flex align-items-center gap-2"
                    >
                        <HugeiconsIcon size={16} icon={Edit02Icon} />
                        {t("EDIT")}
                    </Link>
                ),
            },
            {
                type: "divider",
            },
            {
                key: "delete",
                label: (
                    <div className="d-flex align-items-center gap-2">
                        <HugeiconsIcon size={16} icon={DeleteIcon} />
                        {t("DELETE")}
                    </div>
                ),
                onClick: () => confirm({
                    title: t("CONFIRM_DELETE_JOB_SECTOR_TITLE"),
                    content: t("CONFIRM_DELETE_JOB_SECTOR_MESSAGE"),
                    okText: t("CONFIRM"),
                    cancelText: t("CANCEL"),
                    onOk: ({ closeModal }) => {
                        deleteJobSector(
                            { id },
                            {
                                onSuccess: () => {
                                    closeModal();
                                },

                            }
                        );

                        return false;
                    },
                }),
                danger: true,
            },
        ];
    };

    // Create button component
    const createButton = (
        <BtnAsLink

            className="btn-primary"
            icon={<HugeiconsIcon icon={AddSquareIcon} />}
            link="create"
            disabled={loading}
        >
            {t("CREATE_SECTOR")}
        </BtnAsLink>
    );

    return (
        <Container fluid>
            <ModalComponent loading={isDeletingJobSector} />
            <TableC
                data={jobSectors}
                columns={columns}
                loading={loading}
                searchValue={searchValue}
                onSearchChange={handleSearchChange}
                searchPlaceholder={t("SEARCH_JOB_SECTORS_PLACEHOLDER")}
                filterFields={filterFields}
                filters={filters}
                onFilterApply={handleFilterApply}
                onFilterReset={handleFilterReset}
                pagination={
                    meta?.total != null
                        ? {
                              current: currentPage,
                              total: meta.total,
                              pageSize: meta.per_page ?? PER_PAGE,
                              onChange: handlePageChange,
                          }
                        : undefined
                }
                actions={getActions}
                renderActions={createButton}
                emptyState={EmptyState}
                minVisibleColumns={2}
                skeletonCount={3}
            />
        </Container>
    );
};
